import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NOTIFICATIONS_CHANGED_EVENT, notificationsApi } from '../api/notifications'
import { Badge, IconButton } from './ui'

/**
 * F-043 (REQ-267-001) — the "always visible" unread indicator. Rendered once from `Topbar` in
 * `App.tsx`, same "mounted outside `<Routes>`, persists across navigation" placement as
 * `GlobalSearch`.
 *
 * Polling, not push: `GET /api/notifications/unread-count` is a single indexed count query, cheap
 * enough to poll, but polling every second (or on every route change) would still be needless load
 * on every authenticated tab open in the building — see this task's own brief, "non interrogare il
 * server ogni secondo". `POLL_MS` below is the compromise: frequent enough that a fresh notification
 * shows up within a minute without the user doing anything, rare enough that it costs nothing
 * measurable. Paused outright while the tab is in the background (`document.hidden` /
 * `visibilitychange`, the standard Page Visibility API) and forced to refresh + resume the moment
 * the tab regains focus, so a user who alt-tabs back after an hour sees the true count immediately
 * rather than waiting up to another `POLL_MS`.
 */
const POLL_MS = 60_000

export function NotificationBell() {
  const navigate = useNavigate()
  const [count, setCount] = useState<number | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(() => {
    notificationsApi.unreadCount()
      .then(r => setCount(r.count))
      .catch(() => {
        // Deliberately silent: this is a topbar indicator, not the notifications page itself — a
        // transient failure here just leaves the badge at its last known value (or hidden, on the
        // very first load) instead of putting an error banner in chrome that's present on every
        // page. NotificationsPage.tsx is where a real, actionable error is shown.
      })
  }, [])

  useEffect(() => {
    function startPolling() {
      if (intervalRef.current !== null) return
      intervalRef.current = setInterval(refresh, POLL_MS)
    }
    function stopPolling() {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    function handleVisibilityChange() {
      if (document.hidden) {
        stopPolling()
      } else {
        refresh()
        startPolling()
      }
    }

    refresh()
    if (!document.hidden) startPolling()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    // Any mark-read/mark-all-read/archive action anywhere in the app (today: NotificationsPage)
    // dispatches this so the badge doesn't wait up to POLL_MS to catch up with an action the user
    // just took themselves.
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh)

    return () => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh)
    }
  }, [refresh])

  const hasUnread = count !== null && count > 0

  // Accessibility: the count must be announced as text, not conveyed only by a colored dot — the
  // visible badge is `aria-hidden` and the real information lives in `aria-label` on the button
  // itself, read by a screen reader as e.g. "Notifiche: 3 non lette".
  const label = hasUnread
    ? `Notifiche: ${count} non ${count === 1 ? 'letta' : 'lette'}`
    : 'Notifiche: nessuna non letta'

  return (
    <span className="md-notification-bell">
      <IconButton icon="notifications" aria-label={label} onClick={() => navigate('/notifications')} />
      {/* Il conteggio è già nel nome accessibile del bottone: il badge è solo visivo. */}
      {hasUnread && count !== null && <Badge value={count} aria-hidden="true" className="md-notification-bell__badge" />}
    </span>
  )
}
